/**
 * Transaction identity and dedupe keys.
 *
 * Statements overlap, so every import must decide "have I seen this row before"
 * with no false positives (losing a real transaction) and no false negatives
 * (double-counting). Two strategies, in priority order:
 *
 * 1. **Issuer reference.** Isracard prints מס' שובר, a unique per-transaction
 *    voucher number. When an issuer gives us one, that IS the identity. It
 *    survives re-exports and correctly distinguishes two identical purchases
 *    made at the same merchant on the same day.
 *
 * 2. **Composite fallback**, for issuers with no reference column: person, card,
 *    date, amount, merchant, installment index — plus an occurrence counter.
 *    The counter is what stops two legitimate identical same-day purchases from
 *    collapsing into one; without it, buying coffee twice silently loses a row.
 */

import type { InstallmentInfo } from './types.ts';

export const DEDUPE_KEY_VERSION = 'v1';

export interface DedupeInput {
  personId: string;
  cardId: string;
  transactionDate: string;
  amountILS: number;
  merchantNormalized: string;
  installment: InstallmentInfo | null;
  /** Issuer's own transaction reference, when the format provides one. */
  reference: string | null;
  /**
   * 0-based index among rows in the SAME source file that would otherwise
   * produce an identical composite key. Ignored when `reference` is present.
   */
  occurrence: number;
}

function installmentPart(installment: InstallmentInfo | null): string {
  return installment ? `${installment.current}/${installment.total}` : '-';
}

/**
 * Build the dedupe key. Reference-based and composite keys carry different
 * prefixes so they can never collide with each other.
 */
export function buildDedupeKey(input: DedupeInput): string {
  if (input.reference !== null && input.reference.length > 0) {
    return [DEDUPE_KEY_VERSION, 'ref', input.reference].join('|');
  }
  return [
    DEDUPE_KEY_VERSION,
    'cmp',
    input.transactionDate,
    input.amountILS.toFixed(2),
    input.merchantNormalized,
    installmentPart(input.installment),
    String(input.occurrence),
  ].join('|');
}

/**
 * Dedupe key for a hand-entered row.
 *
 * Keyed on a fresh uid rather than on the row's contents, which is the whole
 * point of the third prefix. Two consequences, both wanted:
 *
 * 1. **It can never collide with an imported row's key.** Typing in a
 *    transaction that also exists in a statement — or re-creating one that was
 *    deleted — produces a different id, so it is not swallowed by that row's
 *    tombstone. A content-derived key would land on exactly the id the
 *    tombstone names, and the row would vanish at the next sync.
 * 2. **Entering the same purchase twice yields two rows.** There is no file to
 *    re-import here, so a repeat is a deliberate act, not an accident to fold
 *    away.
 */
export function buildManualDedupeKey(uid: string): string {
  return [DEDUPE_KEY_VERSION, 'manual', uid].join('|');
}

/**
 * Composite key ignoring the reference, used to spot rows that look identical
 * but carry different references — a sign the issuer re-numbered vouchers
 * between exports. Those go to the review queue instead of being imported
 * blindly as new.
 */
export function buildLooseKey(input: DedupeInput): string {
  return [
    DEDUPE_KEY_VERSION,
    'loose',
    input.transactionDate,
    input.amountILS.toFixed(2),
    input.merchantNormalized,
    installmentPart(input.installment),
  ].join('|');
}

/**
 * FNV-1a 64-bit over UTF-8, hex. Not cryptographic — this is an index key, and
 * the readable dedupe key is stored alongside it for debugging. 64 bits over a
 * dataset of tens of thousands of rows has negligible collision probability.
 */
export function hash64(input: string): string {
  const OFFSET = 0xcbf29ce484222325n;
  const PRIME = 0x100000001b3n;
  const MASK = 0xffffffffffffffffn;

  const bytes = new TextEncoder().encode(input);
  let hash = OFFSET;
  for (const byte of bytes) {
    hash = ((hash ^ BigInt(byte)) * PRIME) & MASK;
  }
  return hash.toString(16).padStart(16, '0');
}

export function transactionIdFromKey(dedupeKey: string): string {
  return hash64(dedupeKey);
}

/** SHA-256 of file bytes, used to detect an identical re-upload. */
export async function hashFileBytes(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Assign occurrence indices across a batch of rows sharing a source file.
 * Mutates nothing — returns the counter for each row in order.
 */
export function assignOccurrences(
  rows: readonly Omit<DedupeInput, 'occurrence'>[],
): number[] {
  const seen = new Map<string, number>();
  return rows.map((row) => {
    const key = buildLooseKey({ ...row, occurrence: 0 });
    const next = seen.get(key) ?? 0;
    seen.set(key, next + 1);
    return next;
  });
}
